import {Injectable} from '@angular/core';
import {ActionsSubject, Store} from '@ngrx/store';
import {
  attachSegmentsToTaxonomy,
  loadAllSegmentsForTaxonomyAttachment,
  loadParentSegments,
  loadSegments, loadSegmentsForExport,
  loadSegmentsForExportPreview,
  loadSegmentsForTaxonomy,
  reloadSegmentListWithProps,
  SegmentsListFilter, SegmentsListFilterPage,
  selectionChanged,
  selectSegments,
  selectSegmentsForTaxonomyAttachment,
  selectTaxonomySegments
} from './segments.state';
import {Observable} from 'rxjs';
import {Segment} from './segment.model';
import {SegmentSearchFilterModel} from './list/search/search-filter.model';
import {ofType} from '@ngrx/effects';
import {map} from 'rxjs/operators';
import {ApiVersion} from '../app.types';
import {TaxonomyFlatNode} from '../taxonomy/taxonomy.component';
import {Page} from '../components/common-interfaces';

@Injectable({
  providedIn: 'root'
})
export class SegmentFacadeService {

  constructor(private store: Store,
              private actions: ActionsSubject) {
  }

  reloadSegments(): Observable<'reload' | 'reload-with-selection-clear'> {
    return this.actions.pipe(ofType(reloadSegmentListWithProps), map(event => event.props ? 'reload-with-selection-clear' : 'reload'));
  }

  getSegments(): Observable<Page<Segment>> {
    return this.store.select(selectSegments);
  }


  loadSegments(currentFilter: SegmentSearchFilterModel, filterModel: Partial<SegmentsListFilter>,
               page: SegmentsListFilterPage, apiVersion: ApiVersion) {
    this.store.dispatch(loadSegments({filter: this.prepareFilter(currentFilter, filterModel),page, apiVersion}));
  }

  loadSegmentsForExportPreview(currentFilter: SegmentSearchFilterModel, apiVersion: ApiVersion) {
    this.store.dispatch(loadSegmentsForExportPreview({
      filter: this.prepareFilter(currentFilter),
      filterSearchValues: currentFilter,
      apiVersion
    }));
  }

  loadSegmentsForExport(currentFilter: SegmentSearchFilterModel, apiVersion: ApiVersion) {
    this.store.dispatch(loadSegmentsForExport({
      filter: this.prepareFilter(currentFilter),
      filterSearchValues: currentFilter,
      apiVersion
    }));
  }

  selectionChanged(selectedSegments: number[]) {
    this.store.dispatch(selectionChanged({selectedIds: selectedSegments}));
  }

  loadSegmentsForCreationOrEdit(filter: SegmentSearchFilterModel) {
    this.store.dispatch(loadParentSegments({
      filter: this.prepareFilter(filter)
    }));
  }

  fetchSegmentsForTaxonomyAttachment() {
    this.store.dispatch(loadAllSegmentsForTaxonomyAttachment({
      filter: this.prepareFilter({})
    }));
  }

  loadSegmentsForTaxonomy(taxonomyId: number) {
    this.store.dispatch(loadSegmentsForTaxonomy({
      filter: {...this.prepareFilter({}), taxonomyIds: [taxonomyId]},
      taxonomyId
    }));
  }

  getSegmentsForTaxonomy(taxonomyId: number): Observable<Segment[]> {
    return this.store.select(selectTaxonomySegments(String(taxonomyId)));
  }

  getAllSegmentsForTaxonomyAttachment(): Observable<Segment[]> {
    return this.store.select(selectSegmentsForTaxonomyAttachment);
  }

  attachToTaxonomy(taxonomy: TaxonomyFlatNode, segmentIds: number[]) {
    this.store.dispatch(attachSegmentsToTaxonomy({taxonomyId: taxonomy.id, segmentIds}));
  }

  private prepareFilter(currentFilter: SegmentSearchFilterModel, filterModel: Partial<SegmentsListFilter> = {}): SegmentsListFilter {
    return <SegmentsListFilter>{
      segmentIds: currentFilter.segmentIds,
      category: currentFilter.category,
      clientSideId: currentFilter.clientSideId,
      distributionAccountIds: (currentFilter.accounts || []).map(it => Number(it.id)),
      segmentName: currentFilter.name,
      segmentMainTypes: currentFilter.segmentMainTypes,
      segmentSubTypes: currentFilter.segmentSubTypes,
      segmentAudienceTypes: currentFilter.segmentAudienceTypes,
      ttdCompliance: currentFilter.ttdCompliance,
      partnerId: currentFilter.partnerId,
      state: currentFilter.state,
      applyFilterToChildren: currentFilter.applyFilterToChildren,
      withPlatformAttributes: currentFilter.withPlatformAttributes,
      taxonomyIds: (currentFilter.taxonomies || []).map(it => it.id),
      ...filterModel
    };
  }
}
